import React, { useEffect, useState } from 'react';
import { Bell, MessageSquare, Phone, Gamepad2, X } from 'lucide-react';

export interface NotificationData {
  id: string;
  type: 'message' | 'call' | 'game' | 'system';
  title: string;
  message: string;
  avatar?: string;
  action?: () => void;
}

interface NotificationProps {
  notifications: NotificationData[];
  onDismiss: (id: string) => void;
}

function NotificationItem({ notif, onDismiss }: { notif: NotificationData; onDismiss: (id: string) => void }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(notif.id);
    }, 5000);
    return () => clearTimeout(timer);
  }, [notif.id, onDismiss]);

  return (
    <div
      key={notif.id}
      className="bg-[#040711] border border-white/10 rounded-xl p-3 shadow-2xl flex items-start gap-3 animate-in slide-in-from-right-8 fade-in duration-300 relative group"
    >
      <div className={`p-2 rounded-lg shrink-0 ${
        notif.type === 'message' ? 'bg-blue-500/20 text-blue-400' :
        notif.type === 'call' ? 'bg-emerald-500/20 text-emerald-400' :
        notif.type === 'game' ? 'bg-purple-500/20 text-purple-400' :
        'bg-amber-500/20 text-amber-400'
      }`}>
        {notif.type === 'message' && <MessageSquare size={16} />}
        {notif.type === 'call' && <Phone size={16} />}
        {notif.type === 'game' && <Gamepad2 size={16} />}
        {notif.type === 'system' && <Bell size={16} />}
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="text-xs font-bold text-white uppercase tracking-wider truncate">{notif.title}</h4>
        <p className="text-[10px] text-white/50 line-clamp-2">{notif.message}</p>
        {notif.action && (
          <button
            onClick={() => { notif.action!(); onDismiss(notif.id); }}
            className="mt-2 text-[10px] font-bold text-[var(--neon)] hover:underline"
          >
            View
          </button>
        )}
      </div>

      <button
        onClick={() => onDismiss(notif.id)}
        className="absolute top-2 right-2 p-1 text-white/20 hover:text-white/80 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X size={12} />
      </button>
    </div>
  );
}

export default function Notification({ notifications, onDismiss }: NotificationProps) {
  return (
    <div className="fixed top-4 right-4 z-[200] flex flex-col gap-2 w-80">
      {notifications.map((notif) => (
        <NotificationItem key={notif.id} notif={notif} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
